package screens.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import com.example.readingnotes.resources.Strings
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import utils.setSessionItem

/**
 * Admin Login Screen with Tab UI
 *
 * This screen provides a tab-based interface to switch between:
 * - Username+Password login (existing functionality)
 * - Email login
 * - OIDC login (authentication platform)
 *
 * Each tab maintains its own state independently.
 */
@Composable
fun AdminLoginScreen(
    onLoginSuccess: () -> Unit,
    onBackClick: () -> Unit,
    onRegisterClick: () -> Unit = {},
    onEmailLoginClick: () -> Unit = {},
    authViewModel: AuthViewModel,
) {
    val state by authViewModel.state.collectAsState()
    var selectedTab by remember { mutableStateOf(AdminLoginTab.USERNAME_PASSWORD) }
    var oidcInitiated by remember { mutableStateOf(false) }

    // Navigate to editor on successful login
    LaunchedEffect(state.isLoggedIn) {
        if (state.isLoggedIn) {
            onLoginSuccess()
        }
    }

    // Handle OIDC redirect
    LaunchedEffect(state.oidcAuthorizationUrl) {
        if (state.oidcAuthorizationUrl != null && selectedTab == AdminLoginTab.OIDC && !oidcInitiated) {
            oidcInitiated = true
            // Store state and code_verifier in sessionStorage
            state.oidcState?.let { setSessionItem("oidc_state", it) }
            state.oidcCodeVerifier?.let { setSessionItem("oidc_code_verifier", it) }
            // Redirect to authorization server
            js("window.location.href = state.oidcAuthorizationUrl")
        }
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        // Back button
        Button({
            classes(AppStyleSheet.backButton)
            onClick { onBackClick() }
        }) { Text("← 戻る") }

        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) { Text(Strings.Auth.login) }

            // Tab Navigation
            Div({
                style {
                    property("display", "flex")
                    property("margin-bottom", "24px")
                    property("border-bottom", "1px solid #dee2e6")
                }
                attr("role", "tablist")
            }) {
                // Username+Password Login Tab
                Button({
                    style {
                        property("flex", "1")
                        property("padding", "12px 8px")
                        property("background-color", if (selectedTab == AdminLoginTab.USERNAME_PASSWORD) "#007bff" else "transparent")
                        property("color", if (selectedTab == AdminLoginTab.USERNAME_PASSWORD) "white" else "#495057")
                        property("border", "none")
                        property("border-bottom", if (selectedTab == AdminLoginTab.USERNAME_PASSWORD) "2px solid #007bff" else "2px solid transparent")
                        property("cursor", "pointer")
                        property("font-size", "13px")
                        property("font-weight", if (selectedTab == AdminLoginTab.USERNAME_PASSWORD) "600" else "400")
                        property("transition", "all 0.2s")
                    }
                    attr("role", "tab")
                    attr("aria-selected", if (selectedTab == AdminLoginTab.USERNAME_PASSWORD) "true" else "false")
                    attr("aria-controls", "username-password-login-panel")
                    attr("id", "username-password-login-tab")
                    attr("tabindex", if (selectedTab == AdminLoginTab.USERNAME_PASSWORD) "0" else "-1")
                    onClick {
                        selectedTab = AdminLoginTab.USERNAME_PASSWORD
                        oidcInitiated = false
                    }
                }) {
                    Text("ユーザー名+パスワード")
                }

                // Email Login Tab
                Button({
                    style {
                        property("flex", "1")
                        property("padding", "12px 8px")
                        property("background-color", if (selectedTab == AdminLoginTab.EMAIL) "#007bff" else "transparent")
                        property("color", if (selectedTab == AdminLoginTab.EMAIL) "white" else "#495057")
                        property("border", "none")
                        property("border-bottom", if (selectedTab == AdminLoginTab.EMAIL) "2px solid #007bff" else "2px solid transparent")
                        property("cursor", "pointer")
                        property("font-size", "13px")
                        property("font-weight", if (selectedTab == AdminLoginTab.EMAIL) "600" else "400")
                        property("transition", "all 0.2s")
                    }
                    attr("role", "tab")
                    attr("aria-selected", if (selectedTab == AdminLoginTab.EMAIL) "true" else "false")
                    attr("aria-controls", "email-login-panel")
                    attr("id", "email-login-tab")
                    attr("tabindex", if (selectedTab == AdminLoginTab.EMAIL) "0" else "-1")
                    onClick {
                        selectedTab = AdminLoginTab.EMAIL
                        oidcInitiated = false
                    }
                }) {
                    Text("メールでログイン")
                }

                // OIDC Login Tab
                Button({
                    style {
                        property("flex", "1")
                        property("padding", "12px 8px")
                        property("background-color", if (selectedTab == AdminLoginTab.OIDC) "#007bff" else "transparent")
                        property("color", if (selectedTab == AdminLoginTab.OIDC) "white" else "#495057")
                        property("border", "none")
                        property("border-bottom", if (selectedTab == AdminLoginTab.OIDC) "2px solid #007bff" else "2px solid transparent")
                        property("cursor", "pointer")
                        property("font-size", "13px")
                        property("font-weight", if (selectedTab == AdminLoginTab.OIDC) "600" else "400")
                        property("transition", "all 0.2s")
                    }
                    attr("role", "tab")
                    attr("aria-selected", if (selectedTab == AdminLoginTab.OIDC) "true" else "false")
                    attr("aria-controls", "oidc-login-panel")
                    attr("id", "oidc-login-tab")
                    attr("tabindex", if (selectedTab == AdminLoginTab.OIDC) "0" else "-1")
                    onClick {
                        selectedTab = AdminLoginTab.OIDC
                    }
                }) {
                    Text("認証基盤")
                }
            }

            // Tab Panels
            Div({
                style {
                    property("min-height", "280px")
                }
            }) {
                when (selectedTab) {
                    AdminLoginTab.USERNAME_PASSWORD -> UsernamePasswordTabContent(authViewModel, state.isLoading)
                    AdminLoginTab.EMAIL -> EmailTabContent(onEmailLoginClick)
                    AdminLoginTab.OIDC -> OidcTabContent(authViewModel, state.isLoading)
                }
            }

            // Register link (only show for username+password tab)
            if (selectedTab == AdminLoginTab.USERNAME_PASSWORD) {
                Div({ classes(AppStyleSheet.registerLink) }) {
                    Text(Strings.Auth.noAccount)
                    A(attrs = {
                        classes(AppStyleSheet.linkStyle)
                        onClick { onRegisterClick() }
                    }) { Text(Strings.Auth.registerLink) }
                }
            }
        }
    }
}

/**
 * Username+Password Tab Content (original AdminLoginScreen content)
 */
@Composable
private fun UsernamePasswordTabContent(
    authViewModel: AuthViewModel,
    isLoading: Boolean,
) {
    val state by authViewModel.state.collectAsState()

    Div({
        attr("role", "tabpanel")
        attr("id", "username-password-login-panel")
        attr("aria-labelledby", "username-password-login-tab")
        attr("tabindex", "0")
    }) {
        // Username input field
        Div({ classes(AppStyleSheet.formGroup) }) {
            Label("admin-username-input") {
                Text(Strings.Auth.username)
            }
            Span({
                style {
                    property("color", Color("#dc3545"))
                    property("margin-left", "4px")
                }
                attr("aria-hidden", "true")
            }) { Text("*") }

            Input(type = InputType.Text) {
                id("admin-username-input")
                classes(AppStyleSheet.textInput)
                value(state.usernameInput)
                attr("autocomplete", "username")
                attr("required", "true")
                attr("aria-label", Strings.Auth.ariaUsername)
                onInput { authViewModel.onEvent(AuthEvent.OnUsernameChanged(it.value)) }
                if (isLoading) {
                    disabled()
                }
            }
        }

        // Password input field
        Div({ classes(AppStyleSheet.formGroup) }) {
            Label("admin-password-input") {
                Text(Strings.Auth.password)
            }
            Span({
                style {
                    property("color", Color("#dc3545"))
                    property("margin-left", "4px")
                }
                attr("aria-hidden", "true")
            }) { Text("*") }

            Input(type = InputType.Password) {
                id("admin-password-input")
                classes(AppStyleSheet.textInput)
                value(state.passwordInput)
                attr("autocomplete", "current-password")
                attr("required", "true")
                attr("aria-label", Strings.Auth.ariaPassword)
                onInput { authViewModel.onEvent(AuthEvent.OnPasswordChanged(it.value)) }
                if (isLoading) {
                    disabled()
                }
            }
        }

        // Error message display
        if (state.errorMessage != null) {
            Div({
                classes(AppStyleSheet.formError)
                attr("role", "alert")
                attr("aria-live", "assertive")
            }) { Text(state.errorMessage!!) }
        }

        // Login button with loading state
        Div({ classes(AppStyleSheet.loginButtonGroup) }) {
            Button({
                classes(AppStyleSheet.submitButton)
                onClick {
                    authViewModel.onEvent(AuthEvent.OnLoginWithUsernameSubmit)
                }
                if (isLoading) {
                    disabled()
                }
            }) {
                Text(if (isLoading) Strings.Auth.loginLoading else Strings.Auth.loginButton)
            }
        }
    }
}

/**
 * Email Login Tab Content (link to EmailLoginScreen)
 */
@Composable
private fun EmailTabContent(onEmailLoginClick: () -> Unit) {
    Div({
        attr("role", "tabpanel")
        attr("id", "email-login-panel")
        attr("aria-labelledby", "email-login-tab")
        attr("tabindex", "0")
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("min-height", "280px")
        }
    }) {
        P({
            style {
                property("color", "#495057")
                property("margin-bottom", "24px")
                property("font-size", "14px")
                property("text-align", "center")
            }
        }) {
            Text("メールアドレスと認証コードを使用してログインします")
        }

        Button({
            classes(AppStyleSheet.submitButton)
            style {
                property("min-height", "44px")
                property("width", "100%")
                property("max-width", "300px")
                property("font-size", "16px")
                property("font-weight", "600")
            }
            attr("aria-label", "メールでログイン")
            onClick { onEmailLoginClick() }
        }) {
            Text("メールログイン画面へ")
        }

        P({
            style {
                property("font-size", "12px")
                property("color", "#6c757d")
                property("margin-top", "16px")
                property("text-align", "center")
            }
        }) {
            Text("クリックするとメールログイン画面に移動します")
        }
    }
}

/**
 * OIDC Login Tab Content
 */
@Composable
private fun OidcTabContent(
    authViewModel: AuthViewModel,
    isLoading: Boolean,
) {
    Div({
        attr("role", "tabpanel")
        attr("id", "oidc-login-panel")
        attr("aria-labelledby", "oidc-login-tab")
        attr("tabindex", "0")
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("min-height", "280px")
        }
    }) {
        P({
            style {
                property("color", "#495057")
                property("margin-bottom", "24px")
                property("font-size", "14px")
                property("text-align", "center")
            }
        }) {
            Text("認証基盤のアカウントでログインします")
        }

        // OIDC Login Button
        Button({
            classes(AppStyleSheet.submitButton)
            style {
                property("min-height", "44px")
                property("width", "100%")
                property("max-width", "300px")
                property("font-size", "16px")
                property("font-weight", "600")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "8px")
            }
            attr("aria-label", "認証基盤でログイン")
            onClick {
                authViewModel.onEvent(AuthEvent.OnOidcLoginInitiate)
            }
            if (isLoading) {
                disabled()
            }
        }) {
            if (isLoading) {
                // Loading spinner
                Span({
                    style {
                        property("display", "inline-block")
                        property("width", "16px")
                        property("height", "16px")
                        property("border", "2px solid #ffffff")
                        property("border-top", "2px solid transparent")
                        property("border-radius", "50%")
                        property("animation", "spin 1s linear infinite")
                    }
                    attr("role", "status")
                    attr("aria-hidden", "true")
                }) {}
                Text("　処理中...")
            } else {
                // Authentication platform icon (generic key icon)
                Span({
                    style {
                        property("font-size", "20px")
                    }
                    attr("aria-hidden", "true")
                }) {
                    Text("🔑")
                }
                Text("認証基盤でログイン")
            }
        }

        // Help text
        P({
            style {
                property("font-size", "12px")
                property("color", "#6c757d")
                property("margin-top", "16px")
                property("text-align", "center")
            }
        }) {
            Text("クリックすると認証基盤のログインページにリダイレクトされます")
        }

        // Additional info
        Div({
            style {
                property("margin-top", "24px")
                property("padding", "12px")
                property("background-color", "#f8f9fa")
                property("border-radius", "4px")
                property("max-width", "400px")
                property("font-size", "12px")
                property("color", "#495057")
            }
            attr("role", "note")
            attr("aria-label", "補足情報")
        }) {
            Text("※ 認証基盤のアカウントをお持ちでない場合は、まず認証基盤でアカウントを作成してください")
        }
    }
}

/**
 * Enum representing admin login tabs
 */
private enum class AdminLoginTab {
    USERNAME_PASSWORD,
    EMAIL,
    OIDC,
}
